<template>
    <div class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm" data-testid="wizard-preview-card">
        <!-- Style-aware header bar -->
        <div :class="headerBarClass" class="h-2"></div>

        <div class="p-4">
            <div class="flex items-center justify-between mb-1">
                <h4 class="text-xs font-bold text-gray-400 uppercase tracking-wider">Live Preview</h4>
                <span v-if="styleCode === 'detailed'" class="text-[10px] text-purple-600 flex items-center gap-1">
                    <i class="pi pi-chart-pie"></i> Performance chart included
                </span>
            </div>
            <p class="font-semibold text-gray-800 mb-3 truncate">
                {{ templateName || 'Untitled Template' }}
            </p>

            <div v-if="columns.length === 0"
                class="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
                <i class="pi pi-sparkles text-2xl mb-2 block text-gray-300"></i>
                Your report card builds up here as you answer.
            </div>

            <template v-else>
                <!-- Sample results table -->
                <div class="overflow-x-auto">
                    <table class="w-full text-xs border border-gray-200">
                        <thead>
                            <tr :class="tableHeadClass">
                                <th class="px-2 py-1.5 text-left font-semibold border-b border-gray-200">Subject</th>
                                <th v-for="col in columns" :key="col.key"
                                    class="px-2 py-1.5 text-center font-semibold border-b border-gray-200 whitespace-nowrap">
                                    {{ col.label }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in rows" :key="row.subject" class="odd:bg-white even:bg-gray-50">
                                <td class="px-2 py-1.5 text-gray-700 border-b border-gray-100">{{ row.subject }}</td>
                                <td v-for="col in columns" :key="col.key"
                                    class="px-2 py-1.5 text-center text-gray-600 border-b border-gray-100">
                                    {{ row[col.key] }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Summaries -->
                <div v-if="summaries.length > 0" class="flex flex-wrap gap-2 mt-3">
                    <span v-for="summary in summaries" :key="summary.key"
                        class="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-1 rounded text-[11px]">
                        {{ summary.label }}
                    </span>
                </div>

                <!-- Feedback boxes -->
                <div v-if="feedbacks.length > 0" class="mt-3 space-y-2">
                    <div v-for="feedback in feedbacks" :key="feedback.label"
                        class="border border-gray-200 rounded p-2">
                        <p class="text-[11px] font-semibold text-gray-500">{{ feedback.label }}</p>
                        <div class="h-3 mt-1 border-b border-dashed border-gray-200"></div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Grade } from '@/types/composition-functions';
import {
  buildPreviewRows,
  type WizardColumnDraft,
  type WizardFeedbackEntry,
  type WizardSummaryDraft,
} from '@/utils/resultTemplateWizard';

const props = defineProps<{
    templateName: string;
    columns: WizardColumnDraft[];
    summaries: WizardSummaryDraft[];
    feedbacks: WizardFeedbackEntry[];
    styleCode: string;
    gradeBands: Grade[] | null;
}>();

const rows = computed(() => buildPreviewRows(props.columns, props.gradeBands));

const headerBarClass = computed(() => {
  if (props.styleCode === 'modern') return 'bg-gradient-to-r from-primary via-purple-500 to-pink-500';
  if (props.styleCode === 'detailed') return 'bg-gradient-to-r from-purple-600 to-indigo-500';
  return 'bg-slate-700';
});

const tableHeadClass = computed(() => {
  if (props.styleCode === 'modern') return 'bg-primary/10 text-primary-800';
  if (props.styleCode === 'detailed') return 'bg-purple-50 text-purple-900';
  return 'bg-gray-100 text-gray-700';
});
</script>
